import React from 'react';
import Icon from '../../../components/AppIcon';
import { getDisplayedProcessingFee } from '../../../utils/feeDisplayUtils';

const BookingSummaryPanel = ({ bookingDetails, paymentQuote, isPaymentRequired, isQuoteLoading, requiresPriceFinalization = false }) => {
  const { date, time, service } = bookingDetails;
  const upfrontPercentage = paymentQuote?.upfrontPaymentPercentage == null
    ? null
    : Number(paymentQuote.upfrontPaymentPercentage);
  const showUpfrontPayment = isPaymentRequired && !isQuoteLoading &&
    upfrontPercentage !== null && Number.isFinite(upfrontPercentage) &&
    upfrontPercentage >= 0 && upfrontPercentage <= 100;

  const formatDate = (dateString) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString)?.toLocaleDateString('en-US', options);
  };

  return (
    <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-xl p-4 md:p-6 lg:p-8 border border-primary/20">
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
          <Icon name="Calendar" size={20} color="white" />
        </div>
        <h3 className="font-heading font-semibold text-lg md:text-xl lg:text-2xl text-foreground">
          Booking Summary
        </h3>
      </div>
      {isPaymentRequired && !requiresPriceFinalization && (
        <div className="mt-4 rounded-xl bg-card p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Payment due now</p>
          {isQuoteLoading ? (
            <div className="mt-3 h-16 animate-pulse rounded-lg bg-muted" />
          ) : paymentQuote ? (
            <div className="mt-3 space-y-2 text-sm font-caption">
              <div className="flex justify-between gap-3 text-muted-foreground">
                <span>{String(paymentQuote.paymentKind).toLowerCase() === 'deposit' ? `Deposit (${Number(paymentQuote.upfrontPaymentPercentage) || 0}%)` : 'Amount due now'}</span>
                <span>GH¢{Number(paymentQuote.subtotal).toFixed(2)}</span>
              </div>
              <div className="flex justify-between gap-3 text-muted-foreground">
                <span>Processing fee</span>
                <span>GH¢{getDisplayedProcessingFee(paymentQuote).toFixed(2)}</span>
              </div>
              <div className="flex justify-between gap-3 border-t border-border pt-2 font-semibold text-foreground">
                <span>Total charge</span>
                <span className="text-primary">GH¢{Number(paymentQuote.total).toFixed(2)}</span>
              </div>
              {Number(paymentQuote.balanceAfterPayment) > 0 && (
                <div className="flex justify-between gap-3 text-xs text-muted-foreground">
                  <span>Balance after payment</span>
                  <span>GH¢{Number(paymentQuote.balanceAfterPayment).toFixed(2)}</span>
                </div>
              )}
            </div>
          ) : (
            <p className="mt-2 text-xs text-muted-foreground">The secure payment page will show the backend-calculated charge.</p>
          )}
        </div>
      )}
      {requiresPriceFinalization && (
        <div className="mb-4 rounded-xl border border-warning/20 bg-warning/10 p-4">
          <div className="flex items-start gap-3">
            <Icon name="BadgeDollarSign" size={19} className="mt-0.5 flex-shrink-0 text-warning" />
            <div>
              <p className="text-sm font-semibold text-foreground font-caption">Payment starts after pricing</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground font-caption">
                The business will confirm the final service price within the displayed range before payment is available.
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="space-y-3 md:space-y-4">
        <div className="flex items-start gap-3 p-3 md:p-4 bg-card rounded-lg">
          <Icon name="Calendar" size={20} className="text-primary mt-1 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs md:text-sm text-muted-foreground font-caption mb-1">Date</p>
            <p className="font-caption font-medium text-sm md:text-base text-foreground break-words">
              {formatDate(date)}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3 p-3 md:p-4 bg-card rounded-lg">
          <Icon name="Clock" size={20} className="text-primary mt-1 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs md:text-sm text-muted-foreground font-caption mb-1">Time</p>
            <p className="font-caption font-medium text-sm md:text-base text-foreground">
              {time}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3 p-3 md:p-4 bg-card rounded-lg">
          <Icon name="Sparkles" size={20} className="text-primary mt-1 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs md:text-sm text-muted-foreground font-caption mb-1">Service</p>
            <p className="font-caption font-medium text-sm md:text-base text-foreground break-words">
              {service}
            </p>
          </div>
        </div>

        {showUpfrontPayment && (
          <div className="flex items-start gap-3 p-3 md:p-4 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg border border-primary/20">
            <Icon name="Percent" size={20} className="text-primary mt-1 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs md:text-sm text-muted-foreground font-caption mb-1">Upfront payment</p>
              <p className="font-caption font-semibold text-sm md:text-base text-primary">
                {upfrontPercentage}% of your service price
              </p>
              <p className="mt-1 text-xs md:text-sm text-muted-foreground font-caption leading-relaxed">
                {upfrontPercentage === 100
                  ? 'You’re paying the full service price upfront.'
                  : upfrontPercentage === 0
                    ? 'No service payment is required upfront.'
                    : 'You’re paying a deposit now. The remaining balance is payable later.'}
                {' '}Processing fees are shown in the breakdown above.
              </p>
            </div>
          </div>
        )}
      </div>
      <div className="mt-4 md:mt-6 p-3 md:p-4 bg-card/50 rounded-lg border border-border">
        <div className="flex items-start gap-2">
          <Icon name="Info" size={16} className="text-muted-foreground mt-0.5 flex-shrink-0" />
          <p className="text-xs md:text-sm text-muted-foreground font-caption leading-relaxed">
            Final price may vary based on additional services or products selected during your appointment.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BookingSummaryPanel;
